import { ShaderProgram } from './ShaderProgram';
import { BrickTexture } from './BrickTexture';
import vertexSource from './shaders/vertex.vert';
import fragmentSource from './shaders/arkanoid.frag';
import {
  C64_PALETTE_GL,
  PLAYFIELD,
  VIRTUAL_WIDTH,
  VIRTUAL_HEIGHT,
  MAX_BALLS,
  MAX_CAPSULES,
  MAX_LASERS,
  MAX_PARTICLES,
} from '../core/Constants';

// Import level background images
import bgLevel1 from '../assets/images/backgrounds/bg_level1.jpg';
import bgLevel2 from '../assets/images/backgrounds/bg_level2.jpg';
import bgLevel3 from '../assets/images/backgrounds/bg_level3.jpg';
import bgLevel4 from '../assets/images/backgrounds/bg_level4.jpg';
import bgLevel5 from '../assets/images/backgrounds/bg_level5.jpg';
import bgLevel6 from '../assets/images/backgrounds/bg_level6.jpg';
import bgLevel7 from '../assets/images/backgrounds/bg_level7.jpg';
import bgLevel8 from '../assets/images/backgrounds/bg_level8.jpg';
import bgLevel9 from '../assets/images/backgrounds/bg_level9.jpg';
import bgLevel10 from '../assets/images/backgrounds/bg_level10.jpg';

const BG_SOURCES = [
  bgLevel1, bgLevel2, bgLevel3, bgLevel4, bgLevel5,
  bgLevel6, bgLevel7, bgLevel8, bgLevel9, bgLevel10,
];

/**
 * High-performance WebGL Renderer powered 100% by fragment shaders
 */
export class WebGLRenderer {
  constructor(canvas) {
    this.canvas = canvas;
    const contextOpts = {
      alpha: false,
      depth: false,
      stencil: false,
      antialias: false,
      preserveDrawingBuffer: false,
    };

    this.gl = this.canvas.getContext('webgl2', contextOpts) ||
              this.canvas.getContext('webgl', contextOpts) ||
              this.canvas.getContext('experimental-webgl', contextOpts);

    if (!this.gl) {
      throw new Error('WebGL is not supported on this browser/hardware.');
    }

    const gl = this.gl;
    this.shader = new ShaderProgram(gl, vertexSource, fragmentSource);
    this.brickTexture = new BrickTexture(gl);

    this.crtEnabled = 0;
    this.flashScreen = 0.0;

    // Background textures per level
    this.bgTextures = [];
    this.currentBgTexture = null;
    this.bgTexturesLoaded = false;
    this.loadBackgroundTextures();

    // Full-screen Quad geometry (Triangle Strip)
    this.quadBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer);
    const vertices = new Float32Array([
      -1.0, -1.0,
       1.0, -1.0,
      -1.0,  1.0,
       1.0,  1.0,
    ]);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

    // Setup uniform data buffers for batching
    this.ballBuffer = new Float32Array(MAX_BALLS * 4);
    this.capsuleBuffer = new Float32Array(MAX_CAPSULES * 4);
    this.laserBuffer = new Float32Array(MAX_LASERS * 4);
    this.paletteFlat = new Float32Array(16 * 3);
    for (let i = 0; i < 16; i++) {
      this.paletteFlat[i * 3 + 0] = C64_PALETTE_GL[i][0];
      this.paletteFlat[i * 3 + 1] = C64_PALETTE_GL[i][1];
      this.paletteFlat[i * 3 + 2] = C64_PALETTE_GL[i][2];
    }
  }

  loadBackgroundTextures() {
    const gl = this.gl;
    this.bgTextures = [];

    BG_SOURCES.forEach((src, index) => {
      const tex = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, tex);

      // Placeholder 1x1 transparent pixel until image loads
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE,
        new Uint8Array([0, 0, 0, 0]));

      const img = new Image();
      img.onload = () => {
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        tex._loaded = true;
      };
      img.src = src;

      this.bgTextures.push(tex);
    });
  }

  getBackgroundTexture(level) {
    const index = Math.max(0, Math.min(level - 1, this.bgTextures.length - 1));
    const tex = this.bgTextures[index];
    return (tex && tex._loaded) ? tex : null;
  }

  toggleCRT() {
    this.crtEnabled = this.crtEnabled === 1 ? 0 : 1;
    return this.crtEnabled === 1;
  }

  triggerFlash(amount = 1.0) {
    this.flashScreen = amount;
  }

  render(sceneState) {
    const gl = this.gl;
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);

    if (this.flashScreen > 0) {
      this.flashScreen = Math.max(0, this.flashScreen - sceneState.dt * 4.0);
    }

    this.shader.use();

    // Bind full-screen quad vertex attribute
    const aPos = this.shader.getAttribLocation('a_position');
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer);
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    // Upload brick texture data
    this.brickTexture.updateFromBricks(sceneState.bricks);
    this.brickTexture.bind(0);
    this.shader.set1i('u_brick_texture', 0);

    // Bind level background texture on texture unit 1
    const bgTex = this.getBackgroundTexture(sceneState.level);
    const gl2 = this.gl;
    if (bgTex) {
      gl2.activeTexture(gl2.TEXTURE1);
      gl2.bindTexture(gl2.TEXTURE_2D, bgTex);
      this.shader.set1i('u_bg_texture', 1);
      this.shader.set1i('u_bg_loaded', 1);
      gl2.activeTexture(gl2.TEXTURE0);
    } else {
      this.shader.set1i('u_bg_loaded', 0);
    }

    // Set core uniforms
    this.shader.set2f('u_resolution', VIRTUAL_WIDTH, VIRTUAL_HEIGHT);
    this.shader.set1f('u_time', sceneState.time);
    this.shader.set4f(
      'u_playfield',
      PLAYFIELD.left,
      PLAYFIELD.top,
      PLAYFIELD.right,
      PLAYFIELD.bottom
    );

    // Paddle uniform
    const paddle = sceneState.paddle;
    this.shader.set4f('u_paddle', paddle.x, paddle.y, paddle.width, paddle.height);
    this.shader.set4f(
      'u_paddle_state',
      paddle.laserActive ? 1.0 : 0.0,
      paddle.catchActive ? 1.0 : 0.0,
      paddle.expandedActive ? 1.0 : 0.0,
      0.0
    );

    // Balls uniform array
    const balls = sceneState.balls;
    const ballCount = Math.min(balls.length, MAX_BALLS);
    for (let i = 0; i < MAX_BALLS; i++) {
      if (i < ballCount) {
        const b = balls[i];
        this.ballBuffer[i * 4 + 0] = b.x;
        this.ballBuffer[i * 4 + 1] = b.y;
        this.ballBuffer[i * 4 + 2] = b.radius;
        this.ballBuffer[i * 4 + 3] = b.speed;
      } else {
        this.ballBuffer[i * 4 + 0] = 0;
        this.ballBuffer[i * 4 + 1] = 0;
        this.ballBuffer[i * 4 + 2] = 0;
        this.ballBuffer[i * 4 + 3] = 0;
      }
    }
    this.shader.set1i('u_ball_count', ballCount);
    this.shader.set4fv('u_balls', this.ballBuffer);

    // Collectibles uniform array
    const capsules = sceneState.capsules;
    const capCount = Math.min(capsules.length, MAX_CAPSULES);
    for (let i = 0; i < MAX_CAPSULES; i++) {
      if (i < capCount) {
        const c = capsules[i];
        this.capsuleBuffer[i * 4 + 0] = c.x;
        this.capsuleBuffer[i * 4 + 1] = c.y;
        this.capsuleBuffer[i * 4 + 2] = c.getTypeIndex();
        this.capsuleBuffer[i * 4 + 3] = c.rotation;
      } else {
        this.capsuleBuffer[i * 4 + 0] = 0;
        this.capsuleBuffer[i * 4 + 1] = 0;
        this.capsuleBuffer[i * 4 + 2] = 0;
        this.capsuleBuffer[i * 4 + 3] = 0;
      }
    }
    this.shader.set1i('u_capsule_count', capCount);
    this.shader.set4fv('u_capsules', this.capsuleBuffer);

    // Lasers uniform array
    const lasers = sceneState.lasers;
    const laserCount = Math.min(lasers.length, MAX_LASERS);
    for (let i = 0; i < MAX_LASERS; i++) {
      if (i < laserCount) {
        const l = lasers[i];
        this.laserBuffer[i * 4 + 0] = l.x;
        this.laserBuffer[i * 4 + 1] = l.y;
        this.laserBuffer[i * 4 + 2] = 0;
        this.laserBuffer[i * 4 + 3] = 0;
      } else {
        this.laserBuffer[i * 4 + 0] = 0;
        this.laserBuffer[i * 4 + 1] = 0;
        this.laserBuffer[i * 4 + 2] = 0;
        this.laserBuffer[i * 4 + 3] = 0;
      }
    }
    this.shader.set1i('u_laser_count', laserCount);
    this.shader.set4fv('u_lasers', this.laserBuffer);

    // Particles uniform array
    const particles = sceneState.particleSystem;
    this.shader.set1i('u_particle_count', Math.min(particles.particles.length, MAX_PARTICLES));
    this.shader.set4fv('u_particles', particles.getUniformData());

    // C64 Palette uniform array
    this.shader.set3fv('u_palette', this.paletteFlat);

    // Level, CRT, and Screen Flash uniforms
    this.shader.set1i('u_level', sceneState.level);
    this.shader.set1i('u_crt_enabled', this.crtEnabled);
    this.shader.set1f('u_flash_screen', this.flashScreen);

    // Execute 100% Fragment Shader render pass
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }
}
